import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const HEALTH = { green: ["On Track", "bg-emerald-100 text-emerald-700"], yellow: ["Needs Attention", "bg-amber-100 text-amber-700"], red: ["Blocked", "bg-rose-100 text-rose-700"], gray: ["Not Started", "bg-slate-100 text-slate-500"] };
const CARDS = [
  ["newly_signed", "Newly Signed", "FileSignature", null], ["welcome_pending", "Welcome Pending", "Mail", null],
  ["portal_pending", "Portal Pending", "MailWarning", "portal_pending"], ["portal_activated", "Portal Activated", "ShieldCheck", null],
  ["organizer_not_started", "Organizer Not Started", "ClipboardX", null], ["organizer_in_progress", "Organizer In Progress", "ClipboardPen", null],
  ["organizer_completed", "Organizer Completed", "ClipboardCheck", null], ["documents_outstanding", "Docs Outstanding", "FileX2", null],
  ["documents_awaiting_review", "Docs Awaiting Review", "FileSearch", null], ["blocked", "Blocked Clients", "OctagonAlert", "blocked"],
  ["production_ready", "Production Ready", "BadgeCheck", "production_ready"], ["transferred", "Transferred", "Send", "transferred"],
  ["overdue", "Overdue Follow-ups", "Clock", null],
];

function Card({ children, className = "" }) { return <div className={`bg-white rounded-xl border border-slate-200 shadow-sm ${className}`}>{children}</div>; }

export default function ClientSuccess() {
  const [cards, setCards] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [backfill, setBackfill] = useState(false);

  const loadCards = useCallback(() => api.get("/client-success/dashboard").then((r) => setCards(r.data.cards)).catch(() => {}), []);
  const loadList = useCallback(() => {
    const q = filter ? `?filter=${filter}` : "";
    api.get(`/client-success/activations${q}`).then((r) => setRows(r.data.items)).catch(() => {});
  }, [filter]);
  useEffect(() => { loadCards(); }, [loadCards]);
  useEffect(() => { loadList(); }, [loadList]);

  const refresh = () => { loadCards(); loadList(); };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="client-success-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Client Success</h1>
          <p className="text-sm text-slate-500 mt-0.5">Activate signed engagements, prepare the work, and gate them into Production.</p>
        </div>
        <button onClick={() => setBackfill(true)} data-testid="cs-backfill-open"
          className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-lg px-4 py-2"><Icons.PlayCircle className="h-4 w-4" />Activate Portal</button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {CARDS.map(([key, label, icon, flt]) => {
          const Ic = Icons[icon] || Icons.Circle;
          const active = filter === flt && flt;
          return (
            <button key={key} onClick={() => flt && setFilter(active ? null : flt)} data-testid={`cs-card-${key}`}
              className={`text-left rounded-xl border p-3.5 transition-colors ${active ? "border-blue-500 bg-blue-50" : "border-slate-200 bg-white hover:border-slate-300"} ${flt ? "cursor-pointer" : "cursor-default"}`}>
              <div className="flex items-center justify-between"><Ic className="h-4 w-4 text-slate-400" /><span className="text-2xl font-bold text-slate-900">{cards?.[key] ?? "—"}</span></div>
              <div className="text-xs text-slate-500 mt-1.5">{label}</div>
            </button>
          );
        })}
      </div>

      <Card>
        <div className="px-5 py-3.5 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-semibold text-slate-900">Activations {filter && <span className="text-sm font-normal text-blue-600">· {filter.replace(/_/g, " ")}</span>}</h2>
          {filter && <button onClick={() => setFilter(null)} className="text-xs text-slate-500 hover:text-slate-800">Clear filter</button>}
        </div>
        {rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400">No activations yet. Sign an engagement or use “Run Activation”.</div>
        ) : (
          <table className="w-full text-sm" data-testid="cs-activations-table">
            <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">
              <th className="px-5 py-2.5 font-medium">Engagement</th><th className="px-3 py-2.5 font-medium">Client</th>
              <th className="px-3 py-2.5 font-medium">Case Mgr</th><th className="px-3 py-2.5 font-medium">Health</th>
              <th className="px-3 py-2.5 font-medium">Status</th><th className="px-3 py-2.5 font-medium">Next Best Action</th><th></th></tr></thead>
            <tbody>
              {rows.map((a) => {
                const [hl, hc] = HEALTH[a.health?.level] || HEALTH.gray;
                return (
                  <tr key={a.id} onClick={() => setOpenId(a.id)} data-testid={`cs-row-${a.id}`} className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer">
                    <td className="px-5 py-3 font-mono text-xs text-slate-700">{a.engagement_number}</td>
                    <td className="px-3 py-3 text-slate-800">{a.client_name}</td>
                    <td className="px-3 py-3 text-slate-500">{a.case_manager_name || "—"}</td>
                    <td className="px-3 py-3"><span className={`text-xs px-2 py-0.5 rounded-full ${hc}`}>{hl}</span></td>
                    <td className="px-3 py-3"><span className="text-xs text-slate-600 capitalize">{(a.production_ready_status || "").replace(/_/g, " ")}</span></td>
                    <td className="px-3 py-3 text-xs text-slate-500 max-w-[220px] truncate">{a.next_best_action}</td>
                    <td className="px-3 py-3 text-right pr-5"><Icons.ChevronRight className="h-4 w-4 text-slate-300 inline" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {openId && <ActivationDetail id={openId} onClose={() => setOpenId(null)} onChange={refresh} />}
      {backfill && <BackfillDialog onClose={() => setBackfill(false)} onDone={refresh} />}
    </div>
  );
}

function BackfillDialog({ onClose, onDone }) {
  const [items, setItems] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/client-success/pending-activation").then((r) => setItems(r.data.items)).catch(() => setItems([])); }, []);

  const openPreview = async (eid) => {
    const { data } = await api.get(`/client-success/engagements/${eid}/activation-preview`);
    setPreview({ eid, ...data });
  };
  const run = async () => {
    setBusy(true);
    try { await api.post(`/client-success/engagements/${preview.eid}/activate`, { force: false }); toast.success("Activation complete"); onDone(); onClose(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="cs-backfill-dialog">
        <DialogHeader><DialogTitle>{preview ? "Confirm Activation" : "Run Activation — Signed Engagements"}</DialogTitle></DialogHeader>
        {!preview ? (
          items === null ? <div className="py-8 flex justify-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500" /></div> :
          items.length === 0 ? <p className="text-sm text-slate-500 py-4">All signed engagements are already activated. 🎉</p> : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {items.map((e) => (
                <button key={e.id} onClick={() => openPreview(e.id)} data-testid={`cs-pending-${e.id}`}
                  className="w-full flex items-center justify-between rounded-lg border border-slate-200 hover:border-blue-400 px-3.5 py-2.5 text-left">
                  <div><div className="font-mono text-xs text-slate-600">{e.engagement_number}</div><div className="text-sm text-slate-800">{e.client_name}</div></div>
                  <Icons.ChevronRight className="h-4 w-4 text-slate-300" />
                </button>
              ))}
            </div>
          )
        ) : (
          <div className="space-y-3 text-sm" data-testid="cs-backfill-preview">
            <p className="text-slate-600">This will create or update Client Success for <span className="font-medium">{preview.engagement_number}</span> ({preview.client_name}):</p>
            <ul className="space-y-1.5 text-slate-700">
              <li className="flex gap-2"><Icons.ClipboardList className="h-4 w-4 text-blue-500 mt-0.5" />Assign organizer: <span className="font-medium">{preview.will_assign_organizer}</span></li>
              <li className="flex gap-2"><Icons.FileText className="h-4 w-4 text-blue-500 mt-0.5" />Request documents: {preview.will_request_documents.join(", ")}</li>
              <li className="flex gap-2"><Icons.ShieldCheck className="h-4 w-4 text-blue-500 mt-0.5" />Portal: {preview.portal_action}</li>
              <li className="flex gap-2"><Icons.UserCheck className="h-4 w-4 text-blue-500 mt-0.5" />Lifecycle: {preview.lifecycle_change}</li>
            </ul>
            {preview.already_activated && <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-xs">An activation already exists — existing onboarding data (checklist, waivers) is preserved and not overwritten.</p>}
          </div>
        )}
        <DialogFooter>
          {preview && <button onClick={() => setPreview(null)} className="text-sm text-slate-500 mr-auto">← Back</button>}
          <button onClick={onClose} className="text-sm px-3 py-2 rounded-lg border border-slate-200">Cancel</button>
          {preview && <button onClick={run} disabled={busy} data-testid="cs-backfill-confirm" className="text-sm px-4 py-2 rounded-lg bg-blue-600 text-white font-medium disabled:opacity-60">{busy ? "Running…" : "Confirm & Activate"}</button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ActivationDetail({ id, onClose, onChange }) {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [waive, setWaive] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get(`/client-success/activations/${id}`).then((r) => setD(r.data)).catch(() => {}), [id]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, key) => { setBusy(key); try { await fn(); await load(); onChange(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(""); } };
  const a = d?.activation;
  const [hl, hc] = a ? (HEALTH[a.health?.level] || HEALTH.gray) : ["", ""];

  return (
    <Sheet open onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="cs-detail-panel">
        {!d ? <div className="py-20 flex justify-center"><Icons.Loader2 className="h-6 w-6 animate-spin text-blue-500" /></div> : (
          <>
            <SheetHeader><SheetTitle className="flex items-center gap-2"><span className="font-mono text-sm">{a.engagement_number}</span><span className={`text-xs px-2 py-0.5 rounded-full ${hc}`}>{hl} · {a.health?.score}</span></SheetTitle></SheetHeader>
            <div className="mt-4 space-y-5 text-sm">
              <div><div className="font-semibold text-slate-900">{d.client.full_name}</div><div className="text-xs text-slate-500">{d.client.email} · {d.client.country_of_residence || "—"}</div>
                <div className="text-xs text-slate-500 mt-1">Next: <span className="text-slate-700">{a.next_best_action}</span></div></div>

              {/* Pipeline */}
              <Section title="Client Success Pipeline" icon="Route">
                <div className="flex items-center gap-1 flex-wrap">
                  {d.pipeline?.stages.map((s) => {
                    const done = d.workflow?.stage_history?.some((h) => h.stage === s.key);
                    const cur = d.workflow?.current_stage === s.key;
                    return <span key={s.key} className={`text-[10px] px-2 py-1 rounded ${cur ? "bg-blue-600 text-white" : done ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>{s.label}</span>;
                  })}
                </div>
              </Section>

              {/* Blockers */}
              {a.blockers?.length > 0 && (
                <Section title="Blockers" icon="OctagonAlert">
                  <div className="space-y-1.5" data-testid="cs-blockers">
                    {a.blockers.map((b) => <div key={b.key} className="flex items-center gap-2 text-xs"><span className={`h-2 w-2 rounded-full ${b.severity === "high" ? "bg-rose-500" : b.severity === "medium" ? "bg-amber-500" : "bg-slate-400"}`} />{b.label}</div>)}
                  </div>
                </Section>
              )}

              {/* Organizer */}
              {d.organizer && (
                <Section title={`Organizer — ${d.organizer.name}`} icon="ClipboardList"
                  action={<button onClick={() => navigate(`/client-success/organizers/${d.organizer.id}`)} data-testid="cs-open-organizer" className="text-[11px] text-blue-600 hover:underline">Open organizer →</button>}>
                  <div className="space-y-2" data-testid="cs-organizer-summary">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden"><div className="h-full bg-blue-600" style={{ width: `${d.organizer_state?.progress ?? d.organizer.progress ?? 0}%` }} /></div>
                      <span className="text-xs font-medium text-slate-600">{d.organizer_state?.progress ?? d.organizer.progress ?? 0}%</span>
                    </div>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-500 capitalize">{(d.organizer.status || "").replace(/_/g, " ")} · {(d.organizer.completion_method || "").replace(/_/g, " ")}</span>
                      {d.organizer_state?.required_remaining > 0 && <span className="text-amber-600">{d.organizer_state.required_remaining} required left</span>}
                    </div>
                  </div>
                </Section>
              )}

              {/* Documents */}
              <Section title="Document Requests" icon="FileText" action={<button onClick={() => act(() => api.post(`/client-success/activations/${id}/documents/refresh`), "refresh")} className="text-[11px] text-blue-600">Refresh</button>}>
                <div className="space-y-1.5" data-testid="cs-doc-requests">
                  {d.document_requests.map((r) => (
                    <div key={r.id} className="flex items-center justify-between">
                      <span className="text-xs text-slate-700">{r.title}</span>
                      <div className="flex items-center gap-2">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${r.request_status === "approved" ? "bg-emerald-100 text-emerald-700" : r.request_status === "missing" ? "bg-rose-100 text-rose-600" : "bg-blue-100 text-blue-600"}`}>{r.request_status.replace(/_/g, " ")}</span>
                        {r.request_status !== "approved" && <button onClick={() => act(() => api.post(`/client-success/activations/${id}/document-requests/${r.id}/review`), `doc-${r.id}`)} data-testid={`cs-doc-approve-${r.id}`} className="text-[11px] text-blue-600 hover:underline">Approve</button>}
                      </div>
                    </div>
                  ))}
                </div>
              </Section>

              {/* Production Gate */}
              <Section title="Production Ready Gate" icon="ShieldCheck">
                <div className="space-y-1.5" data-testid="cs-gate">
                  {d.production_gate.map((g) => (
                    <div key={g.key} className="flex items-center justify-between">
                      <span className="text-xs text-slate-700 flex items-center gap-2">{g.met ? <Icons.CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> : <Icons.Circle className="h-3.5 w-3.5 text-slate-300" />}{g.label}{g.waived ? " (waived)" : ""}</span>
                      {!g.met && !g.waived && <button onClick={() => setWaive(g.key)} data-testid={`cs-waive-${g.key}`} className="text-[11px] text-amber-600 hover:underline">Waive</button>}
                    </div>
                  ))}
                </div>
                {a.waivers?.length > 0 && <div className="mt-2 text-[11px] text-slate-400">Waivers: {a.waivers.map((w) => w.requirement).join(", ")}</div>}
              </Section>

              {/* Actions */}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100">
                {!a.production_team_id && <Btn icon="Users" onClick={() => act(() => api.post(`/client-success/activations/${id}/assign-production`), "prod")} busy={busy === "prod"}>Assign Production</Btn>}
                {a.production_ready_status === "in_progress" && <Btn icon="BadgeCheck" primary disabled={!d.can_mark_ready} onClick={() => act(() => api.post(`/client-success/activations/${id}/production-ready`), "ready")} busy={busy === "ready"}>Mark Production Ready</Btn>}
                {a.production_ready_status === "ready" && <Btn icon="Send" primary onClick={() => act(() => api.post(`/client-success/activations/${id}/transfer`, {}), "xfer")} busy={busy === "xfer"}>Transfer to Production</Btn>}
                {a.production_ready_status === "transferred" && <span className="text-sm text-emerald-600 flex items-center gap-1.5"><Icons.CheckCircle2 className="h-4 w-4" />Transferred to Production</span>}
              </div>
            </div>
            {waive && <WaiveDialog requirement={waive} onClose={() => setWaive(null)} onConfirm={async (reason) => { await act(() => api.post(`/client-success/activations/${id}/waivers`, { requirement: waive, reason }), "waive"); setWaive(null); }} />}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, icon, action, children }) {
  const Ic = Icons[icon] || Icons.Circle;
  return <div><div className="flex items-center justify-between mb-2"><h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5"><Ic className="h-3.5 w-3.5" />{title}</h3>{action}</div>{children}</div>;
}
function Btn({ icon, children, onClick, busy, primary, disabled }) {
  const Ic = Icons[icon] || Icons.Circle;
  return <button onClick={onClick} disabled={busy || disabled} data-testid={`cs-action-${(children + "").toLowerCase().replace(/\s/g, "-")}`}
    className={`inline-flex items-center gap-1.5 text-sm rounded-lg px-3.5 py-2 font-medium disabled:opacity-50 ${primary ? "bg-blue-600 text-white hover:bg-blue-700" : "border border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
    {busy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Ic className="h-4 w-4" />}{children}</button>;
}
function WaiveDialog({ requirement, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="cs-waive-dialog">
        <DialogHeader><DialogTitle>Waive requirement</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Waiving <span className="font-medium text-slate-700">{requirement.replace(/_/g, " ")}</span> requires a logged reason.</p>
        <textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cs-waive-reason" rows={3} className="w-full border border-slate-200 rounded-lg p-2.5 text-sm mt-2" placeholder="Reason for waiver…" />
        <DialogFooter>
          <button onClick={onClose} className="text-sm px-3 py-2 rounded-lg border border-slate-200">Cancel</button>
          <button onClick={() => reason.trim() && onConfirm(reason)} data-testid="cs-waive-confirm" className="text-sm px-4 py-2 rounded-lg bg-amber-600 text-white font-medium">Waive</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
